import type { ItemShelfBandRule } from "@CommonTypes/algo/generated-pogger";

export type SnakingStrategy = "none" | "variant_spanning_sections" | "always";

export type SnakingCascadeCdtLevel = "cdt0" | "cdt1" | "cdt2";

export type ItemsSqueezeOption = "none" | "all";

export type BalanceFacingsMode =
  "REBALANCE_ALL_FACINGS" | "AFFECTED_SHELVES_ONLY" | "EMPTY_SPACE_ONLY" | "NONE";

export type AdjustFacingsMode = "EXACT_MATCH" | "BEST_FIT";

export interface PspRules {
  store_layout_strategy?: "copy_matching_base" | "fresh";
  fixture: {
    can_adjust_shelf_notches: boolean;
    can_add_or_delete_shelves: boolean;
    shelves_same_height_as_base_pog: boolean;
    vertical_overlap: boolean;
    merged_shelf_width_tolerance_pct: number;
    vertical_split_section: number;
    /** Gap from product tops to the shelf above, pog x100 units; absent means 0. */
    finger_space?: number;
  };
  range: {
    can_add_non_core_range_products: boolean;
    can_apply_squeeze_to_placed_products: boolean;
    balance_facings_mode?: BalanceFacingsMode;
    adjust_facings_mode?: AdjustFacingsMode | null;
    /**
     * Weekly movement floor for the candidates sent to pogger: a product whose
     * master `quantity` (units sold per week) is below this is left out of the
     * request altogether. Core-range products and new items (`is_new_item`,
     * which the ETL sets for products introduced in the 90 days before go-live)
     * are never dropped by it. Absent or null sends every product.
     * 7-Eleven Malaysia runs at 0.25.
     */
    min_candidate_quantity?: number | null;
  };
  merchandising: {
    vertical_blocking_mode: boolean;
    can_break_cases_trays_if_core_range_not_fit: boolean;
    can_fill_extra_space_next_to_cases_trays_with_loose_products: boolean;
    how_should_loose_products_be_placed_with_respect_to_their_cases_trays: "BEHIND" | "FRONT";
    leverage_alternative_orientations: boolean;
    /**
     * Add facings high until each product reaches the shelf above. Sent as
     * `postprocessing_options.stack_to_shelf_top`; absent means false.
     */
    stack_to_shelf_top?: boolean;
    /** Facings high may go past each product's max high. Sent as `postprocessing_options.stack_past_max_rows`. */
    stack_past_max_stack?: boolean;
    /**
     * Top caps: units laid on their side across the top of the facings. Sent as
     * `optimization_options.yCapping`; absent sends nothing, so top caps follow zCapping.
     */
    vertical_capping?: boolean;
    /** Top caps also on products with a max high of 1, and on facings with room for another unit high. */
    vertical_capping_past_max_stack?: boolean;
    snaking: boolean;
    cdt_stacking: boolean;
    /** Absent or null turns cascade snaking off. */
    snaking_cascade_cdt_level?: SnakingCascadeCdtLevel | null;
    extra_facing_marginal_value?: number;
    items_squeeze_option: ItemsSqueezeOption;
    /** Which products need shelf dividers. Absent behaves as `source: "none"`. */
    dividers?: DividerPolicy;
    // Horizontal blocking only (DEV-3531). Store generation sends the pogger's
    // `horizontalRectangles` (clean brand blocks) on a horizontally blocked
    // wall; these override its options, per category. Absent: the pogger's
    // defaults (`HORIZONTAL_RECTANGLES_DEFAULTS`). Ignored under vertical blocking.
    /** Extra facings a brand row may take to fill it to the bay seam, as a multiple of max facings by days of supply: 1 to 5, 1 turns the fill off. Absent: double. */
    horizontal_rectangles_overface?: number;
    /** The fewest facings of its widest pack a brand's row is at least: 1 to 10. Absent: 2. A true or false stored before it was a count is ignored. */
    horizontal_rectangles_brands?: number | boolean;
    /** The most an extra facing filling a brand row pays, as a share of the gap it closes: 0 to 1. Absent: half. */
    horizontal_rectangles_shared_row_price?: number;
    /**
     * Vertical blocking only: shelf bands by key, over the default tall-pack band. Null drops
     * that key's band, the default included.
     */
    item_shelf_bands?: Record<string, ItemShelfBandRule | null>;
    /**
     * Hundredths of a shelf's width a shelf may leave empty before it counts as not
     * full; a whole number, 0 or more. Sent as `optimization_options.minRowFillSlack`.
     * Absent: 0 under vertical blocking.
     */
    full_shelf_tolerance?: number;
    /**
     * Every product stands as one rectangle: the same facings on each of its shelves,
     * the shelves next to each other, no L shapes. Sent as
     * `optimization_options.itemRectangles`. Absent: the backend writes true under
     * vertical blocking.
     */
    item_rectangles?: boolean;
    /**
     * Vertical blocking only: solve this merchflow's store planograms one brand block at a time
     * (pogger's `optimization_options.brandBlockPieces`), each brand block on its own mudmap
     * cells, then put back together; pogger solves the whole store if anything fails. Sent only
     * for a store planogram whose brand blocks are held to their mudmap cells and that has at
     * most 9 bays. Absent or false: the whole-store solve.
     */
    brand_block_pieces?: boolean;
  };
  high_density_mode: {
    enabled: boolean;
    place_all_variants: boolean;
    snaking_strategy: SnakingStrategy;
    variant_contiguous: boolean;
    allow_breaking_variant_sequencing: boolean;
    vertical_blocking_mode: boolean;
    /**
     * @deprecated Shown on the rules screen but never read: the backend derives
     * `strictRectangles` from `brand_blocks_min_bays`. Kept so stored rules stay valid.
     */
    strict_rectangles?: boolean;
    /**
     * Under vertical blocking, stores with at least this many bays keep each
     * brand in a wide block of its own (pogger's 2D brand layer); smaller
     * stores may run a brand on into the next bay as a narrow strip. `1` is
     * every store, `"none"` no store. Absent: four bays.
     */
    brand_blocks_min_bays?: number | "none";
    /**
     * Under vertical blocking, the minimum width of a brand block on every shelf of a
     * bay, in packs of its typical product; a whole number, 1 or more. A brand too
     * narrow for it is pooled with its neighbours, else stands on fewer shelves. Sent
     * to the pogger as `mudmap_generation_options.minBrandPacks` and to the store
     * materializer as `brandBlockMinPacks`. Absent: 2 (pallets 1).
     */
    brand_block_min_packs?: number;
    /**
     * On a run with an aisle break, the CDT level that stays whole on one side of a
     * break: no break falls inside a node at this level or below it. `cdt0` allows
     * breaks only between sections, `cdt1` only between cdt1 nodes, `cdt2` only between
     * cdt2 nodes. A brand is always whole, so a level below the brand changes nothing.
     * The run leaves a bay empty or drops products before it cuts a node it must keep.
     * Absent means only the brand stays whole. Sent to the pogger as
     * `splitKeepWholeLevel` on split runs only.
     */
    aisle_break_whole_level?: "cdt0" | "cdt1" | "cdt2";
  };

  high_density_building_blocks_mode: {
    enabled: boolean;
    direction: "FORWARD" | "BACKWARD";
    start_from:
      | {
          type: "BIGGEST" | "LAST_COMPLETED";
        }
      | {
          type: "STORE_CODE";
          store_code: string;
        };
  };

  mudmap_level: "CDT1" | "CDT2" | "ITEM_ID";
  optimize_on: "SALES" | "PROFIT" | "DISPERSION";
  relax_factors: {
    space_mix: number;
    either_or: number | string;
    both_or_none: number | string;
    prereq: number | string;
    shelf_item_count: number | string;
    fixed_notch_num: number | string;
    finger_space: number | string;
    units_not_enough_dos: number;
    units_not_enough_mos: number;
    /** A number is the hard limit at one price; the object may price units over max units in steps. */
    units_too_many: number | UnitsTooManyRule;
    facings_not_enough: number | string;
    facings_too_many: number | string;
    min_width: number | string;
    min_distribution: number;
    width_slack: number | string;
    cdt1_contiguous: number | string;
    cdt2_contiguous: number | string;
    cdt3_contiguous: number | string;
    cdt1_order: number | string;
    cdt2_order: number | string;
    cdt3_order: number | string;
    /** Size tiers as shelf rows; absent or "inf" holds them hard. */
    cdt2_size_tier_rows?: number | string;
    /** The cdt3 order (scent / sub-brand) inside a size tier row; absent or "inf" holds it hard. */
    cdt3_size_tier_order?: number | string;
    item_id_order: number | string;
    item_span: number | string;
    cdt1_span: number | string;
    cdt2_span: number | string;
    cdt3_span: number | string;
    secondary_orientation: number | string;
    rows_not_enough: number | string;
    shelf_segment_space: number;
    /**
     * Price per hundredth of a bay shelf holding products left short of full beyond
     * `merchandising.full_shelf_tolerance`; "inf" makes full shelves hard. Absent:
     * the backend writes 100 under vertical blocking.
     */
    min_row_fill_slack?: number | string;
    /**
     * @deprecated Use `merchandising.full_shelf_tolerance`. Still read by the backend
     * so stored rules keep their tolerance.
     */
    min_row_fill_slack_tolerance?: number | string;
  };
  mudmap_generation_penalty_multiplier?: {
    totalSpaceSlack: number;
    cdt0SectionsSpanning: number;
    cdt0ShelvesSpanning: number;
    cdt2SectionsSpanning: number;
    cdt2ShelvesSpanning: number;
    cdt0RoundingSlack: number;
    cdt2RoundingSlack: number;
    cdt1BreakingSequencing: number;
    cdt2BreakingSequencing: number;
    cdtSpaceMixSlack: number;
    cdt0ShelvesMisaligned: number;
    itemWidthSatisfied: number;
    cdt0ShelfSpaceMisalignment: number;
    cdt2ShelfSpaceMisalignment: number;
    verticalBlockShelfPresence: number;
    cdt0CellXMisalignment: number;
    cdt2CellXMisalignment: number;
  };
  /**
   * Per-run weight post-processing rules, passed to pogger verbatim as
   * `weight_rules`. Optional so every psp_rules JSONB stored before this field
   * existed stays valid; absent means pogger's own defaults, which are inert.
   */
  weight_rules?: WeightRules;
  /**
   * Which CDT store generation follows. The backend contract:
   *
   * - STORE_PRODUCT: each product's store_product chain, the one the latest
   *   ETL wrote, with the store's effective tree filling only the products it
   *   leaves without one. The run pins no CDT Studio release, and the variant
   *   restrictions CDT Studio wrote (groups whose id starts `CDT-STUDIO-`) are
   *   dropped. Restrictions the merchant authored still apply.
   * - CATEGORY_TREE: the base planogram's newest CDT Studio release is pinned
   *   on the run, so its labels and its rule set apply. Without a release, the
   *   store's effective tree, with the store_product chain filling only the
   *   products it leaves without one.
   * - Absent: CATEGORY_TREE when the base planogram has a CDT Studio release,
   *   else STORE_PRODUCT. The backend records the resolved value on the run.
   *
   * The update flow does not read this field; it takes `FlowUpdateCdtSource`
   * instead, and nothing above covers it.
   *
   * Not `generationCdtSource` on `flow_review.config`, which answers a
   * different question — whether a base pog's own item labels override the
   * masters at all.
   */
  cdt_source?: GenerationCdtChainSource;
  /**
   * Store-level rules for the floor pallets of a run. Optional so every
   * psp_rules JSONB stored before this field existed stays valid. Absent, or
   * with every field at its default, the pallet request is what it was before.
   */
  pallets?: PalletRules;
}

/** Mirrors pogger's `MaxUnitsRelaxationFactor`. */
export interface UnitsTooManyRule {
  max_relaxation: number;
  penalty_weight?: number;
  steps?: UnitsTooManyStep[] | null;
}

export interface UnitsTooManyStep {
  /** A fraction over max units, where this step's price ends. */
  up_to: number;
  penalty_weight: number;
}

export interface PalletRules {
  /**
   * How many different products one pallet may carry, 1 to
   * `PALLET_DEFAULT_MAX_SKUS` (utils/pog/pallet), which is also the default.
   * Sent to pogger as each pallet's `max_skus`. There is no minimum to set:
   * value and fill decide what stands, never a floor.
   */
  max_products_per_pallet: number;
  /**
   * How high a pallet may be stacked, in the units of the fixture geometry
   * (hundredths of an inch for the US merchants). Null is each pallet's own
   * height, from its deck to the top of its fixture; set, a pallet stacks to
   * the lower of the two, never higher than its fixture.
   */
  max_stack_height: number | null;
}

/**
 * "none" (or no stored policy) asks no product for dividers, and no shelf gets
 * them: fixture records and base pog shelf dividers are not carried over. Only
 * a shelf set by hand to "always", or one kept by `keepFixtureDividers`, gets
 * dividers. Hand overrides apply under every source.
 * "items" is a list of products picked one by one (`productCodes`).
 */
export type DividerPolicySource = "base" | "attribute" | "cdt" | "items" | "none";

export type DividerCdtLevel = "cdt0" | "cdt1" | "cdt2" | "cdt3";

export const DIVIDER_CDT_LEVELS: readonly DividerCdtLevel[] = ["cdt0", "cdt1", "cdt2", "cdt3"];

/** The flow's one divider size. Lengths in the pog's x100 units, like shelf widths. */
export interface DividerHardware {
  width: number;
  height: number;
  depth: number;
  atStart: boolean;
  atEnd: boolean;
}

/**
 * Which products need dividers. A shelf gets them when more than half of its
 * facing columns belong to such products, then every facing on it does.
 */
export interface DividerPolicy {
  source: DividerPolicySource;
  /** `source: "attribute"`: a key of the category's product attributes. */
  attribute?: string;
  values?: string[];
  /** `source: "cdt"`. */
  cdtLevel?: DividerCdtLevel;
  cdtValues?: string[];
  /** `source: "items"`: the product codes picked one by one. */
  productCodes?: string[];
  /** Null or absent: the base planogram's own divider size. */
  hardware?: DividerHardware | null;
  /**
   * A store shelf whose fixture record already has valid dividers keeps them,
   * as if set to "always" by hand ("never" by hand still wins). Absent = false.
   */
  keepFixtureDividers?: boolean;
  /** Spring loader ticks, independent of the divider ones; ignored under "base", which follows the base. */
  loaderProductCodes?: string[];
  /** The flow's default loader depth (x100); null or absent: each base shelf's own. */
  loaderDepth?: number | null;
  /** Where spring loaders go (DEV-3518). Absent = "products". */
  loaderMode?: LoaderMode;
}

/**
 * "products" ("Follow the products"): every shelf offers a loader and the
 * pogger puts it in where most of the shelf's facings need one. "shelf" ("Stay
 * with the shelf"): only the shelves whose hardware has one (the Fixtures page
 * row, else the base shelf) keep it, whatever lands there. A Fixtures page row
 * of 0 is never and > 0 always, in both.
 */
export type LoaderMode = "products" | "shelf";

export const LOADER_MODES: readonly LoaderMode[] = ["products", "shelf"];

/** Per-shelf hand override on the store's fixture row; null follows the policy. */
export type DividersSetByHand = "always" | "never";

export type GenerationCdtChainSource = "STORE_PRODUCT" | "CATEGORY_TREE";

/**
 * The heavy-below-height rule: a weight AND a height line, or no rule at all.
 *
 * The two numbers are co-dependent, so they live in one required pair — "half a
 * rule" has no shape to arrive in and needs no validator. Mirrors pogger's
 * `HeavyRule`; `WeightRules.heavy_rule` is either null or complete.
 */
export interface HeavyRule {
  /** Grams. A candidate at or above this counts as heavy. */
  item_threshold: number;
  /** Compared against `shelf_y` with NO conversion — see `HeavyRule.max_deck_y` in openapi-pogger.json. */
  max_deck_y: number;
}

/**
 * Weight inputs and the switches for pogger's two post-solve passes. Mirrors
 * pogger's `WeightRules` field for field — deliberately untranslated, because
 * the previous lb/cm translation layer is what drifted out of sync with the
 * contract. Weights are integer grams; lengths are unitless payload geometry.
 *
 * Nothing here reaches the CP-SAT model: both passes run after the solve.
 */
export interface WeightRules {
  /**
   * Grams. The cap for shelves that carry no `weight_limit` of their own.
   * Null means those shelves have no cap and get no verdict — pogger invents
   * none, and neither may any consumer.
   */
  default_shelf_weight_limit: number | null;
  /** Null disables the heavy rule entirely; present, it is complete. */
  heavy_rule: HeavyRule | null;
  /** Pass 1: trim depth, then delete worst value-per-weight, until under cap. */
  resolve_overweight_shelves: boolean;
  /** Pass 2: exchange whole brand blocks so heavy product sits below the line. */
  swap_heavy_brands_below_max_height: boolean;
  /**
   * Which CDT level names the brand block Pass 2 swaps.
   *
   * @deprecated Not a user choice: the backend derives it per run (the
   * coarsest CDT level whose blocks each hold one item-master brand) and
   * ignores any value sent here. Optional only so psp_rules stored while it
   * was asked, and older clients that still send it, stay valid.
   */
  heavy_brand_block_level?: "cdt1" | "cdt2" | "cdt3";
  /**
   * A FRACTION in [0, 1], despite the `_pct` name kept for wire compatibility.
   * Both shelves in a swap must keep at least this share of their pre-swap
   * linear fill. 1 forbids any fill loss; 0 disables the guard.
   */
  swap_fill_floor_pct: number;
  /**
   * Emit the advisory weight block when neither pass runs; no other effect.
   * The backend forces this on while shelf-weight is enabled for the
   * merchant, since the block is the frontend's only source of shelf limits
   * (see `effectiveWeightLimit` on `PlanogramShelf`).
   */
  report: boolean;
}
